import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useUserData } from '../../../hooks/useUserData';
import { useExpenses } from '../../../hooks/useExpenses';
import { buildApiUrl } from '../../../config/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { truncateProjectionData, getChartTruncationConfig } from '../../../utils/chartUtils';
import ChartUpgradePrompt from '../../../components/charts/ChartUpgradePrompt';

const NetWorthChart = () => {
  const [chartType, setChartType] = useState('line');
  const [timeRange, setTimeRange] = useState('25');
  const { userData } = useUserData();
  const { expenses } = useExpenses();
  const { getAuthHeaders } = useAuth();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const { canProjectBeyond5Years } = useSubscription();

  // Safety factor from Setup (multiplier). Default to 4x if missing
  const safetyFactor = Number(userData?.setup?.marginOfSafety ?? 4) || 4;

  const [netWorthData, setNetWorthData] = useState([]);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/dashboard/net-worth${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load net worth');
        const json = await res.json();
        if (json?.success && Array.isArray(json.data)) setNetWorthData(json.data);
        else setNetWorthData([]);
      } catch (e) {
        console.error(e);
        setNetWorthData([]);
      }
    };
    fetchData();
  }, [userData, getDemoQueryParams, getAuthHeaders]);

  const formatCurrency = (value) => {
    // Indian number system: Crore (Cr) and Lakh (L) with sign support
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 10000000) { // 1 Cr = 10,000,000
      return `${sign}₹${(abs / 10000000).toFixed(1)} Cr`;
    }
    if (abs >= 100000) { // 1 Lakh = 100,000
      return `${sign}₹${(abs / 100000).toFixed(1)} L`;
    }
    return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
  };

  // Prepare plotting data: full years for subscribed/demo; for free users show 10 years with blur after 5
  const plotData = useMemo(() => {
    const fullTotalYears = netWorthData.length;
    const truncationConfig = getChartTruncationConfig('NetWorthDashboard', canProjectBeyond5Years, isDemoModeActive);
    const shouldTruncate = truncationConfig.shouldTruncate;
    const freeYearsLimit = 5;
    const displayYearsLimit = freeYearsLimit * 2; // 10 years for free users

    const limitedData = shouldTruncate
      ? netWorthData.slice(0, displayYearsLimit + 1)
      : netWorthData;

    const mappedData = limitedData.map((r, idx) => {
      const baseData = {
        ...r,
        unfundedDeficitNeg: r?.unfundedDeficit ? -Math.abs(Number(r.unfundedDeficit)) : 0
      };

      if (shouldTruncate && idx > freeYearsLimit) {
        const lastKnown = limitedData[freeYearsLimit];
        const trendMultiplier = 1 + (0.08 * (idx - freeYearsLimit));
        return {
          ...baseData,
          corpus: (lastKnown?.corpus || 0) * trendMultiplier,
          liquidCorpus: (lastKnown?.liquidCorpus || 0) * trendMultiplier,
          requirement: (lastKnown?.requirement || 0) * trendMultiplier,
          safetyMargin: (lastKnown?.safetyMargin || 0) * trendMultiplier,
          netWorth: (lastKnown?.netWorth || 0) * trendMultiplier,
          income: lastKnown?.income || 0,
          isBlurred: true
        };
      }
      return { ...baseData, isBlurred: false };
    });

    return { data: mappedData, shouldTruncate, fullTotalYears, freeYearsLimit };
  }, [netWorthData, canProjectBeyond5Years, isDemoModeActive]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const filtered = payload.filter((entry) => {
        const key = entry.dataKey || entry.name;
        if (String(key) === 'unfundedDeficitNeg') {
          const v = Number(entry.value || 0);
          return v < 0; // only show when applicable
        }
        return true;
      });
      return (
        <div className="bg-card border border-border rounded-lg p-4 shadow-floating">
          <p className="font-medium text-foreground mb-2">{`Year: ${label}`}</p>
          {filtered.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${entry.name}: ${formatCurrency(entry.value)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Net Worth Trajectory</h3>
          <p className="text-sm text-muted-foreground">{`${userData?.setup?.projectionHorizon || 20}-year projection with safety factor from Setup`}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex bg-muted rounded-lg p-1">
            <Button
              variant={chartType === 'line' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('line')}
              className="px-3 py-1"
            >
              <Icon name="TrendingUp" size={16} />
            </Button>
            <Button
              variant={chartType === 'area' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setChartType('area')}
              className="px-3 py-1"
            >
              <Icon name="BarChart3" size={16} />
            </Button>
          </div>
        </div>
      </div>

      <div className="relative" style={{ height: '320px' }}>
        {(() => {
          const { data: chartData, shouldTruncate, fullTotalYears, freeYearsLimit } = plotData;
          
          // X-axis labels every 2 years
          const xAxisTicks = [];
          chartData.forEach((d, i) => {
            if (i % 2 === 0) {
              xAxisTicks.push(d.year);
            }
          });

          return (
            <>
              <ResponsiveContainer width="100%" height="100%">
                {chartType === 'line' ? (
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <defs>
                      {/* Gradients for blurred sections */}
                      <linearGradient id="blurGradientPrimary" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="1" />
                        <stop offset="48%" stopColor="var(--color-primary)" stopOpacity="1" />
                        <stop offset="55%" stopColor="var(--color-primary)" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0.15" />
                      </linearGradient>
                      <linearGradient id="blurGradientPurple" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="1" />
                        <stop offset="48%" stopColor="#8B5CF6" stopOpacity="1" />
                        <stop offset="55%" stopColor="#8B5CF6" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.15" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="year" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      ticks={xAxisTicks}
                      height={50}
                    />
                    <YAxis 
                      tickFormatter={formatCurrency}
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <Tooltip 
                      content={(props) => {
                        if (props.active && props.payload && props.payload.length) {
                          const isBlurred = props.payload[0]?.payload?.isBlurred;
                          if (isBlurred) {
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                                <p className="font-medium text-muted-foreground flex items-center gap-2">
                                  <Icon name="Lock" size={14} />
                                  Locked Content
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Upgrade to view full projections
                                </p>
                              </div>
                            );
                          }
                          return <CustomTooltip {...props} />;
                        }
                        return null;
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="corpus" 
                      stroke={shouldTruncate ? 'url(#blurGradientPrimary)' : 'var(--color-primary)'}
                      strokeWidth={3}
                      name="Total Corpus"
                      dot={false}
                      strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "0"}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="liquidCorpus" 
                      stroke={shouldTruncate ? 'url(#blurGradientPurple)' : '#8B5CF6'}
                      strokeWidth={2}
                      name="Liquid-only Corpus"
                      dot={false}
                      strokeDasharray={(entry) => entry?.isBlurred ? "5 5" : "5 5"}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="requirement" 
                      stroke="var(--color-warning)" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name="Requirement"
                      dot={false}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="safetyMargin" 
                      stroke="var(--color-success)" 
                      strokeWidth={2}
                      strokeDasharray="3 3"
                      name={`Safety Margin (${safetyFactor}x)`}
                      dot={false}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="netWorth" 
                      stroke="var(--color-foreground)" 
                      strokeWidth={2}
                      name="Total Net Worth"
                      dot={false}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="unfundedDeficitNeg" 
                      stroke="var(--color-destructive)" 
                      strokeWidth={2}
                      name="Unfunded Deficit"
                      dot={false}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                      yAxisId={0}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="income" 
                      stroke="#0EA5E9" 
                      strokeWidth={2}
                      strokeDasharray="2 2"
                      name="Annual Income"
                      dot={false}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                  </LineChart>
                ) : (
                  <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis 
                      dataKey="year" 
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                      ticks={xAxisTicks}
                      height={50}
                    />
                    <YAxis 
                      tickFormatter={formatCurrency}
                      stroke="var(--color-muted-foreground)"
                      fontSize={12}
                    />
                    <Tooltip 
                      content={(props) => {
                        if (props.active && props.payload && props.payload.length) {
                          const isBlurred = props.payload[0]?.payload?.isBlurred;
                          if (isBlurred) {
                            return (
                              <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
                                <p className="font-medium text-muted-foreground flex items-center gap-2">
                                  <Icon name="Lock" size={14} />
                                  Locked Content
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Upgrade to view full projections
                                </p>
                              </div>
                            );
                          }
                          return <CustomTooltip {...props} />;
                        }
                        return null;
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="safetyMargin" 
                      stroke="var(--color-success)" 
                      fill="var(--color-success)"
                      fillOpacity={(entry) => entry?.isBlurred ? 0.03 : 0.1}
                      name={`Safety Margin (${safetyFactor}x)`}
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="corpus" 
                      stroke="var(--color-primary)" 
                      fill="var(--color-primary)"
                      fillOpacity={(entry) => entry?.isBlurred ? 0.08 : 0.3}
                      name="Total Corpus"
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.3 : 1}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="liquidCorpus" 
                      stroke="#8B5CF6" 
                      fill="#8B5CF6"
                      fillOpacity={(entry) => entry?.isBlurred ? 0.04 : 0.15}
                      name="Liquid-only Corpus"
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="requirement" 
                      stroke="var(--color-warning)" 
                      fill="var(--color-warning)"
                      fillOpacity={(entry) => entry?.isBlurred ? 0.05 : 0.2}
                      name="Requirement"
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="netWorth" 
                      stroke="var(--color-foreground)" 
                      fill="var(--color-foreground)"
                      fillOpacity={(entry) => entry?.isBlurred ? 0.02 : 0.05}
                      name="Total Net Worth"
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="income" 
                      stroke="#0EA5E9" 
                      fill="#0EA5E9"
                      fillOpacity={(entry) => entry?.isBlurred ? 0.03 : 0.1}
                      name="Annual Income"
                      strokeOpacity={(entry) => entry?.isBlurred ? 0.2 : 1}
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>

              {/* Blurred overlay with gradient and CTA */}
              {shouldTruncate && fullTotalYears > freeYearsLimit && (
                <div 
                  className="absolute top-0 right-0 pointer-events-none"
                  style={{ 
                    left: '48%',
                    bottom: '50px',
                    background: 'linear-gradient(to right, transparent 0%, rgba(255, 255, 255, 0.5) 8%, rgba(255, 255, 255, 0.85) 25%, rgba(255, 255, 255, 0.95) 60%)',
                    backdropFilter: 'blur(5px)',
                  }}
                >
                  {/* Dark mode gradient overlay */}
                  <div 
                    className="absolute inset-0 dark:block hidden"
                    style={{ 
                      background: 'linear-gradient(to right, transparent 0%, rgba(0, 0, 0, 0.4) 8%, rgba(0, 0, 0, 0.75) 25%, rgba(0, 0, 0, 0.9) 60%)',
                    }}
                  />
                  
                  {/* Upgrade CTA Card */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto">
                    <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-2xl p-4 w-[180px] text-white">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                          <Icon name="Lock" size={22} className="text-white" />
                        </div>
                      </div>
                      <h4 className="font-bold text-center mb-1 text-sm">
                      {(userData?.setup?.projectionHorizon || 20) - freeYearsLimit}+ Years Locked
                      </h4>
                      <p className="text-xs text-center text-white/90 mb-3 leading-tight">
                        Your future continues here. Upgrade to see clearly.
                      </p>
                      <div className="bg-white/20 backdrop-blur rounded px-2 py-1 mb-3">
                        <p className="text-xs font-medium text-center">✓ 92% users opted for this</p>
                      </div>
                      <a
                        href="/upgrade"
                        className="block w-full bg-white text-amber-600 font-semibold text-sm py-2 px-3 rounded-lg text-center hover:bg-amber-50 transition-colors shadow-md"
                      >
                        See My Full Picture
                      </a>
                    </div>
                  </div>
                  
                  {/* Vertical separator line at truncation point */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-amber-400 to-transparent opacity-70"
                    style={{ 
                      boxShadow: '0 0 12px rgba(251, 191, 36, 0.7)'
                    }}
                  />
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-primary rounded-full"></div>
          <span className="text-sm text-muted-foreground">Total Corpus</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#8B5CF6' }}></div>
          <span className="text-sm text-muted-foreground">Liquid-only Corpus</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-foreground rounded-full"></div>
          <span className="text-sm text-muted-foreground">Total Net Worth</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-warning rounded-full"></div>
          <span className="text-sm text-muted-foreground">Requirement</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-success rounded-full"></div>
          <span className="text-sm text-muted-foreground">{`${safetyFactor}x Safety Margin`}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#0EA5E9' }}></div>
          <span className="text-sm text-muted-foreground">Annual Income</span>
        </div>
        {netWorthData.some((r) => Number(r.unfundedDeficit) > 0) && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-destructive)' }}></div>
            <span className="text-sm text-muted-foreground">Unfunded Deficit</span>
          </div>
        )}
      </div>

    </div>
  );
};

export default NetWorthChart;